import {
  getDefaultValues,
  getEligibleSupportContractOptions,
} from "@features/run/RunModelForm/helpers"
import {
  SAMPLING_FIELD_MAX,
  SAMPLING_FIELD_MIN,
  SEED_FIELD_MAX,
  SEED_FIELD_MIN,
  getValidationSchema,
} from "@features/run/RunModelForm/validation"
import { yupResolver } from "@hookform/resolvers/yup"
import { FormHelperText, MenuItem, Select, Stack, TextField, Typography } from "@mui/material"
import { useAtomValue } from "jotai"
import { permissionsAtom } from "@store/authAtoms"
import { HTMLAttributes } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { getErrorMessage } from "utils/form"
import { InferType } from "yup"
import { GraphDto, UserGraphDto } from "@custom-types/graphs"

export type RunModelFormValues = InferType<ReturnType<typeof getValidationSchema>>

type RunModelFormProps = Omit<HTMLAttributes<HTMLFormElement>, "onSubmit"> & {
  onSubmit: (values: RunModelFormValues) => void
  graph: UserGraphDto | GraphDto
}

const RunModelForm = ({ onSubmit, graph, ...props }: RunModelFormProps) => {
  const permissions = useAtomValue(permissionsAtom)
  const contractOptions = getEligibleSupportContractOptions(
    graph.base_graph_family_key,
    permissions,
  )

  const formMethods = useForm<RunModelFormValues>({
    defaultValues: getDefaultValues(contractOptions),
    resolver: yupResolver(getValidationSchema(graph, permissions)),
  })

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = formMethods

  return (
    <FormProvider {...formMethods}>
      <form autoComplete="off" noValidate {...props} onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="column" spacing={2}>
          <Controller
            name="run_name"
            control={control}
            render={({ field }) => (
              <TextField
                error={Boolean(errors[field.name])}
                helperText={getErrorMessage(errors, field.name)}
                label="Name of run"
                variant="standard"
                sx={{ spacing: 2 }}
                {...field}
              />
            )}
          />

          <Controller
            name="number_of_samplings"
            control={control}
            render={({ field }) => (
              <TextField
                error={Boolean(errors[field.name])}
                helperText={getErrorMessage(errors, field.name)}
                label="Number of sampling iterations"
                variant="standard"
                inputProps={{
                  min: SAMPLING_FIELD_MIN,
                  max: SAMPLING_FIELD_MAX,
                  step: 100,
                  type: "number",
                }}
                {...field}
              />
            )}
          />

          <Controller
            name="seed"
            control={control}
            render={({ field }) => (
              <TextField
                error={Boolean(errors[field.name])}
                helperText={getErrorMessage(errors, field.name)}
                label="Seed"
                variant="standard"
                inputProps={{
                  min: SEED_FIELD_MIN,
                  max: SEED_FIELD_MAX,
                  type: "number",
                }}
                {...field}
              />
            )}
          />

          {contractOptions.length > 1 && (
            <Stack sx={{ mt: 2 }} spacing={1}>
              <Typography variant={"h6"}> You are a member on multiple contracts.</Typography>
              <Typography>Select which contract to assign this model run.</Typography>
              <Typography>
                BEWARE: This affects who can see this sampling run and it's results.
              </Typography>
              <Controller
                name="contract_key"
                control={control}
                render={({ field }) => (
                  <>
                    <Select
                      error={Boolean(errors[field.name])}
                      displayEmpty
                      sx={{ fontSize: "small", mt: 1 }}
                      {...field}
                    >
                      {contractOptions.map((contractKey) => (
                        <MenuItem key={contractKey.contract_key} value={contractKey.contract_key}>
                          {contractKey.contract_key}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error>{getErrorMessage(errors, field.name)}</FormHelperText>
                  </>
                )}
              />
            </Stack>
          )}
          {contractOptions.length == 1 && (
            <Stack sx={{ mt: 2 }} spacing={1}>
              <Typography>Accessible by users on Contract</Typography>
              <Controller
                name="contract_key"
                control={control}
                render={({ field }) => (
                  <>
                    <Select
                      error={Boolean(errors[field.name])}
                      disabled
                      sx={{ fontSize: "small", mt: 1 }}
                      {...field}
                      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
                      value={contractOptions[0].contract_key} // set after immediate above else default=''
                    >
                      <MenuItem
                        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
                        key={contractOptions[0].contract_key}
                        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
                        value={contractOptions[0].contract_key}
                      >
                        {
                          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
                          contractOptions[0].contract_key
                        }
                        {/*{contractOptions[0].families} show the families to help*/}
                      </MenuItem>
                    </Select>
                    <FormHelperText error>{getErrorMessage(errors, field.name)}</FormHelperText>
                  </>
                )}
              />
            </Stack>
          )}
        </Stack>
      </form>
    </FormProvider>
  )
}

export default RunModelForm
